import { useQuery } from "@tanstack/react-query";

import { useAuth } from "@/providers";
import { useDomainServices } from "@/providers/DomainServices";

import type { FeatureFlagName } from "../FeatureFlagName";

export function getQueryKey(orgId?: string) {
  if (orgId !== undefined) {
    return ["featureFlagStates", orgId];
  }
  return ["featureFlagStates"];
}

/**
 * Check whether a single feature flag is enabled for the current user + org.
 *
 * Backed by a shared app-level cache of all flag states — does not make a
 * per-flag network request.
 */
export function useCheckFeatureFlag(flagName: FeatureFlagName): boolean {
  const { data: flagStates } = useAllFeatureFlagStates();
  // TODO(dave): we could cache the feature flag states in localStorage to improve the flag accuracy during initial load.
  return flagStates?.[flagName] ?? false;
}

/**
 * Internal hook that owns the single app-level query for all feature flag states.
 * Automatically refreshes every 60 seconds.
 */
function useAllFeatureFlagStates() {
  const { featureFlagService } = useDomainServices();
  const { currentOrganization } = useAuth();

  const orgId = currentOrganization?.org_id;

  return useQuery<Record<string, boolean>, Error>({
    queryKey: getQueryKey(orgId),
    queryFn({ signal }) {
      if (!orgId) {
        return Promise.reject(new Error("No organization selected"));
      }
      return featureFlagService.checkAllFeatureFlags(orgId, {
        abortSignal: signal,
      });
    },
    enabled: orgId !== undefined,
    staleTime: 60_000,
    refetchInterval: 60_000,
  });
}
